// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_DATA_URL;
const API_ENDPOINT = '/api/workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_DATA_SCOPE;

const HEADERS = {
  'Content-Type': 'application/json',
};

// ============= Types =============

export interface AssignDocumentToCollectionProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaceId: string;
  collectionId: string;
  documentId: string;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Assigns a document to a collection
 * @param getAccessToken Function to acquire access token
 * @param workspaceId Workspace identifier
 * @param collectionId Collection identifier
 * @param documentId Document identifier to assign
 * @returns Promise with API response or error
 */
export const assignDocumentToCollection = async ({
  getAccessToken,
  workspaceId,
  collectionId,
  documentId,
}: AssignDocumentToCollectionProps): Promise<any> => {
  try {
    console.log('🔄 Assigning document to collection...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction - POST to collection with document ID in body
    const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}/collection/${collectionId}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request - body contains document ID to add to collection
    const response = await fetch(convertURI, {
      method: 'POST',
      headers: createHeaders(token),
      body: JSON.stringify([documentId]),
    });

    // Response handling
    if (!response.ok) {
      const errorText = await response.text();
      console.log(`HTTP error! status: ${response.status}`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Document assigned to collection successfully: ${data}`);

    return data;
  } catch (error) {
    console.error('🔴 Error assigning document to collection:', error);
    throw error;
  }
};
